import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck, Sparkles, Zap, MessageSquare, Wallet, Users } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import HeroSearchBar from "@/components/marketplace/HeroSearchBar";
import CategoryCard from "@/components/marketplace/CategoryCard";
import ProviderCard from "@/components/marketplace/ProviderCard";
import HotDealsStrip from "@/components/deals/HotDealsStrip";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const HERO_IMG = "https://images.unsplash.com/photo-1600210492493-0946911123ea?auto=format&fit=crop&w=2000&q=80";

const MODE_CTAS = [
  {
    key: "local",
    icon: MapPin,
    title: "Book Local",
    subtitle: "Cleaners, handymen, movers, lawn care — same day.",
    href: "/book-local",
    testid: "cta-book-local",
    accent: "from-[#165a71] to-[#0f3d4d]",
  },
  {
    key: "digital",
    icon: Sparkles,
    title: "Hire Digital",
    subtitle: "Designers, developers, marketers, editors.",
    href: "/hire-digital",
    testid: "cta-hire-digital",
    accent: "from-[#252A34] to-[#071b22]",
  },
  {
    key: "request",
    icon: MessageSquare,
    title: "Request a Service",
    subtitle: "Describe the job — pros come to you with quotes.",
    href: "/request-a-service",
    testid: "cta-post-request",
    accent: "from-[#37768e] to-[#165a71]",
  },
];

const HOW_IT_WORKS = [
  { icon: Users, title: "Tell us what you need", copy: "Search a category, describe the job, or start with a template. Real, verified pros on both sides — no lead-broker games." },
  { icon: Wallet, title: "Book, request quotes, or hire", copy: "Instant booking for local pros, custom quotes for projects, or long-form gigs like Fiverr — all under one roof." },
  { icon: ShieldCheck, title: "Get it done — with peace of mind", copy: "Escrow-protected payments, ID-verified pros, ratings you can trust, and dispute support if anything goes sideways." },
];

export default function HomePage() {
  useDocumentTitle("Local services & digital talent on the Gulf Coast");
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [featuredProviders, setFeaturedProviders] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [catsLocal, catsDigital, provs, s] = await Promise.all([
          api.get("/categories", { params: { kind: "local", featured_only: true, parent_id: "root" } }),
          api.get("/categories", { params: { kind: "digital", featured_only: true, parent_id: "root" } }),
          api.get("/providers", { params: { limit: 8, sort: "featured" } }),
          api.get("/system/stats"),
        ]);
        const merged = [...catsLocal.data, ...catsDigital.data].slice(0, 12);
        setCategories(merged);
        setFeaturedProviders(provs.data.items || []);
        setStats(s.data);
      } catch (e) {
        // page still renders with empty states
      }
    })();
  }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative -mt-24 min-h-[86vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#071b22]/85 via-[#071b22]/70 to-[#071b22]/85" aria-hidden />
        <div className="absolute inset-0 gcw-noise opacity-40" aria-hidden />

        <div className="relative mx-auto max-w-6xl px-6 lg:px-10 pt-40 pb-24 w-full">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-3.5 py-1.5 text-xs font-medium text-white/85"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Serving the Gulf Coast — FL · AL · MS · LA · TX
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold text-white tracking-tight leading-[1.05]"
              data-testid="hero-headline"
            >
              Get Any Kind of <br className="hidden md:block" />
              <span className="text-[#87bccc]">Work Done.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-5 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed"
            >
              Book local professionals, hire digital experts, or post a custom project —
              from one trusted marketplace built for the Gulf Coast.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10"
            >
              <HeroSearchBar />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 grid gap-4 sm:grid-cols-3"
            >
              {MODE_CTAS.map((m) => {
                const requiresAuth = m.key === "request";
                const href = requiresAuth && !user ? `/login?next=${encodeURIComponent(m.href)}` : m.href;
                return (
                  <Link
                    key={m.key}
                    to={href}
                    data-testid={m.testid}
                    className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${m.accent} p-5 text-white border border-white/10 hover:-translate-y-0.5 transition-transform duration-300`}
                  >
                    <div className="absolute inset-0 gcw-noise opacity-20" aria-hidden />
                    <div className="relative flex items-start gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 backdrop-blur-md">
                        <m.icon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <div className="font-display font-semibold text-lg">{m.title}</div>
                        <div className="text-sm text-white/80 mt-1 leading-relaxed">{m.subtitle}</div>
                      </div>
                      <ArrowRight className="ml-auto h-4 w-4 text-white/70 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS STRIP — INTENTIONALLY REMOVED (owner directive, launch phase).
         Small early-launch numbers hurt credibility. Re-enable when the
         numbers are worth showing by restoring this block. */}

      {/* CATEGORIES */}
      <section className="py-24" data-testid="categories-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Explore</div>
              <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">
                Popular categories on the coast
              </h2>
            </div>
            <Link to="/categories" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#165a71] hover:text-[#11485a]" data-testid="all-categories-link">
              All categories <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {categories.map((c, i) => (
              <CategoryCard key={c.id} category={c} index={i} />
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <HotDealsStrip title="Hot deals on the coast" className="pb-24" />
      </div>

      {/* FEATURED PROVIDERS */}
      <section className="py-24 bg-white border-y border-slate-200" data-testid="featured-providers-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Featured pros</div>
              <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">
                Meet Gulf Coast standouts
              </h2>
            </div>
            <Link to="/providers" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#165a71] hover:text-[#11485a]" data-testid="all-providers-link">
              Browse all pros <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {featuredProviders.map((p, i) => (
              <ProviderCard key={p.id} provider={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-12 items-start">
          <div className="lg:col-span-4">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">How it works</div>
            <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">
              One marketplace. Three ways to get things done.
            </h2>
            <p className="mt-5 text-[#252A34]/75 leading-relaxed">
              Whether it's a leaking faucet in Pensacola or a rebrand for your Baton Rouge coffee shop —
              Gulf Coast Work keeps the whole workflow (search, hire, chat, pay, review) in one place.
            </p>
            <Link
              to="/register"
              data-testid="how-cta-signup"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#071b22] text-white px-6 py-3 font-medium hover:bg-black transition-colors"
            >
              Create your free account <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-8 grid gap-6 sm:grid-cols-1">
            {HOW_IT_WORKS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex gap-5 rounded-2xl bg-white border border-slate-200 p-6 hover:border-[#165a71]/30 transition-colors"
              >
                <div className="h-11 w-11 shrink-0 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center">
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold text-[#071b22]">
                    {i + 1}. {s.title}
                  </div>
                  <p className="mt-1.5 text-[#252A34]/75 leading-relaxed">{s.copy}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BECOME A PROVIDER CTA */}
      <section className="py-24" data-testid="become-provider-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-[#071b22] text-white p-10 md:p-16">
            <div className="absolute inset-0 gcw-noise opacity-30" aria-hidden />
            <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-[#165a71]/40 blur-3xl" aria-hidden />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#87bccc]">
                  For pros
                </div>
                <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold">
                  Get discovered by real customers on the coast.
                </h2>
                <p className="mt-5 text-white/75 max-w-lg leading-relaxed">
                  Whether you clean pool decks in Destin or design brands from Baton Rouge —
                  build a profile once, and get booked from every angle.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/register?intended_role=provider" className="inline-flex items-center gap-2 rounded-full bg-white text-[#071b22] px-6 py-3 font-medium hover:bg-slate-100 transition-colors" data-testid="become-pro-cta">
                    Become a provider <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/become-a-provider" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-medium hover:bg-white/10 transition-colors" data-testid="become-pro-learn-more">
                    How it works
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <ProCallout icon={Zap} title="Zero listing fees" copy="Only pay when you actually get hired." />
                <ProCallout icon={ShieldCheck} title="Buyer-protected" copy="Escrow, insurance & dispute support." />
                <ProCallout icon={Users} title="One profile" copy="Local, digital or agency — unified." />
                <ProCallout icon={Sparkles} title="Free growth tools" copy="Promotions, referrals & featured slots." />
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-display text-3xl font-semibold text-[#071b22]" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
    <div className="mt-1 text-xs text-[#252A34]/60 uppercase tracking-wider">{label}</div>
  </div>
);

const ProCallout = ({ icon: Icon, title, copy }) => (
  <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md p-4">
    <Icon className="h-5 w-5 text-[#87bccc]" />
    <div className="mt-3 font-display font-medium">{title}</div>
    <div className="text-xs text-white/70 mt-1 leading-relaxed">{copy}</div>
  </div>
);
